import { isString, logger, useOnlineStatus } from '@tunasong/models'
import { features, isPermanentAuthError, OAuth, profilesApi, useSelector, useThunkDispatch } from '@tunasong/redux'
import { FullPageProgress } from '@tunasong/ui-lib'
import queryString from 'query-string'
import React, { useEffect, useState } from 'react'
import NotAuthorizedPage from '../pages/401.js'
import { prefetchRouteEntity } from './prefetch-route-entity.js'
import { ProfileNameGate } from './profile-name-gate.js'
import { TARGET_PATH } from './target-path.js'

/**
 * GH #1469: a transient token-refresh failure must not bounce a user who holds
 * a valid refresh token to the login screen. When a stored session exists we
 * retry the refresh up to MAX_REFRESH_ATTEMPTS (spaced by REFRESH_RETRY_DELAY_MS)
 * before giving up — and even then we do NOT redirect to login, because the
 * refresh token is still valid and a reconnect / reload recovers.
 */
const MAX_REFRESH_ATTEMPTS = 4
const REFRESH_RETRY_DELAY_MS = 1500
const wait = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

const AuthGuard: React.FC<{ children: React.ReactNode }> = props => {
  const { children } = props
  const isLoggedIn = useSelector(state => state.user.isLoggedIn)
  const dispatch = useThunkDispatch()

  /** Check if logged in. If not, redirect to federated login screen by calling authorize() */
  const [isAuthorizing, setIsAuthorizing] = useState(true)

  const online = useOnlineStatus()

  useEffect(() => {
    let cancelled = false

    /** Authorize with the IDP (redirects the browser away). */
    const auth = async () => {
      localStorage.setItem(TARGET_PATH, document.location.pathname)
      /** Mode signup? */
      const params = queryString.parse(document.location.search)
      const signup = isString(params.mode) && params.mode === 'signup'
      return OAuth.authorize({ signup })
    }

    const establishSession = async () => {
      if (isLoggedIn) {
        return
      }

      setIsAuthorizing(true)

      /** Check if we have any tokens in localStorage first */
      const hasStoredSession = OAuth.hasSession()

      /** Do not try to refresh if we're offline, but also don't redirect to IDP if we have stored tokens */
      const shouldRefresh = Boolean(online && hasStoredSession)

      // Only the no-stored-session case gets a single shot at the IDP. A stored
      // (refreshable) session is retried so a transient refresh failure can't
      // bounce a valid user to login (GH #1469).
      const maxAttempts = hasStoredSession ? MAX_REFRESH_ATTEMPTS : 1

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        if (cancelled) {
          return
        }
        try {
          const session = await OAuth.getSession(shouldRefresh)
          if (cancelled) {
            return
          }

          if (session?.idToken?.payload?.sub) {
            dispatch(features.user.actions.setAuth(session))
            // RTK Query port
            dispatch(
              profilesApi.util.prefetch('loadProfile', { emailOrUserId: session.idToken.payload.sub }, {})
            )
            // Start the route's entity in parallel with the profile instead of after the app shell (GH #1543)
            prefetchRouteEntity(dispatch, document.location.pathname)
            setIsAuthorizing(false)
            return
          }

          if (!online) {
            /** We have tokens but are offline - wait for connectivity instead of redirecting */
            logger.warn('Offline - waiting for connectivity to complete auth')
            setIsAuthorizing(false)
            return
          }

          if (hasStoredSession) {
            /** Stored refresh token exists but we got no valid session — likely transient. */
            if (attempt < maxAttempts) {
              await wait(REFRESH_RETRY_DELAY_MS)
              continue
            }
            /** GH #1469: do NOT redirect to login; the refresh token is still valid. */
            logger.warn('Could not refresh a stored session; not redirecting to login (GH #1469)')
            setIsAuthorizing(false)
            return
          }

          /** No stored tokens at all → federated login. */
          await auth()
          return
        } catch (e) {
          if (cancelled) {
            return
          }
          if (!online) {
            /** We have tokens but are offline - wait for connectivity */
            logger.warn('Session refresh failed while offline - waiting for connectivity')
            setIsAuthorizing(false)
            return
          }

          /**
           * GH #1538: a permanent failure (invalid_grant — the stored refresh
           * token is expired/revoked) cannot self-heal by retrying. Discard the
           * dead session and re-authenticate even though tokens still sit in
           * localStorage; otherwise the user is stuck on the 401 page forever.
           */
          if (isPermanentAuthError(e)) {
            logger.warn('Refresh token rejected (invalid_grant); re-authenticating (GH #1538)', e)
            /**
             * Clear the dead tokens SYNCHRONOUSLY, then issue exactly one
             * navigation. `OAuth.logout()` does not clear localStorage — it
             * only redirects to the logout proxy, which eventually lands on
             * `/logout` where `clearSession()` finally runs. Calling it here
             * raced `auth()`: two `document.location.replace()` calls in one
             * tick, the second wins, the proxy round-trip never happens and the
             * session is never cleared. `hasSession()` then stays true forever,
             * so every reload refreshes, gets another permanent 401, and the
             * user can never reach login.
             */
            OAuth.clearSession()
            dispatch(features.user.actions.signout())
            await auth().catch(err => logger.error('auth failed', err))
            return
          }

          if (hasStoredSession) {
            if (attempt < maxAttempts) {
              await wait(REFRESH_RETRY_DELAY_MS)
              continue
            }
            /**
             * GH #1469: refresh threw despite a stored refresh token (e.g. a
             * transient non-200 from the token endpoint). Do NOT logout or
             * redirect — that would discard a valid session. Leave it intact; a
             * reconnect / reload recovers.
             */
            logger.warn('Session refresh failed despite a stored refresh token; not redirecting to login (GH #1469)', e)
            setIsAuthorizing(false)
            return
          }

          /** No stored session → hard failure → discard local state + federated login. */
          OAuth.clearSession()
          dispatch(features.user.actions.signout())
          await auth().catch(err => logger.error('auth failed', err))
          return
        }
      }
    }

    logger.debug('AuthGuard: login state or online state changed', { isLoggedIn, online })
    void establishSession()

    return () => {
      cancelled = true
    }
  }, [isLoggedIn, online, dispatch])

  if (isLoggedIn) {
    /** Every signed-in user has a profile name; the gate holds the progress indicator until the profile is known */
    return <ProfileNameGate>{children}</ProfileNameGate>
  }

  return isAuthorizing ? <FullPageProgress /> : <NotAuthorizedPage />
}

export default AuthGuard
